import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AdminLayout } from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { getLeads, updateLead, deleteLead, createLeadAdmin, getVerticals } from "@/lib/api";
import api from "@/lib/api";
import { toast } from "sonner";
import { 
  Users, 
  Mail, 
  Phone, 
  Building, 
  Trash2, 
  Edit, 
  Search,
  RefreshCw,
  MessageSquare,
  Calendar,
  Plus,
  User,
  X,
  Save,
  Loader2,
  ImagePlus
} from "lucide-react";

const statusColors = {
  new: "bg-blue-100 text-blue-800",
  contacted: "bg-yellow-100 text-yellow-800",
  qualified: "bg-purple-100 text-purple-800",
  converted: "bg-green-100 text-green-800",
  lost: "bg-red-100 text-red-800",
};

const companySizes = [
  "1-10",
  "11-50",
  "51-200",
  "201-500",
  "501-1000",
  "1000+"
];

export default function AdminLeads() {
  const { t } = useTranslation();
  const [leads, setLeads] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedLead, setSelectedLead] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editNotes, setEditNotes] = useState("");
  const [editStatus, setEditStatus] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    company_size: "",
    message: "",
    problem_interest: "",
    vertical_interest: ""
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const [leadsRes, verticalsRes] = await Promise.all([
        getLeads(),
        getVerticals()
      ]);
      setLeads(leadsRes.data);
      setVerticals(verticalsRes.data);
    } catch (error) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const handleUpdateLead = async () => {
    if (!selectedLead) return;
    try {
      await updateLead(selectedLead.id, { status: editStatus, notes: editNotes });
      toast.success(t("admin.leads.leadUpdated"));
      setIsDialogOpen(false);
      fetchLeads();
    } catch (error) {
      toast.error(t("admin.leads.failedToUpdate"));
    }
  };

  const handleDeleteLead = async (id) => {
    if (!confirm(t("admin.leads.confirmDelete"))) return;
    try {
      await deleteLead(id);
      toast.success(t("admin.leads.leadDeleted"));
      fetchLeads();
    } catch (error) {
      toast.error(t("admin.leads.failedToDelete"));
    }
  };

  const handleCreateLead = async (e) => {
    e.preventDefault();
    
    if (!createFormData.name || !createFormData.email) {
      toast.error(t("admin.leads.nameRequired"));
      return;
    }

    setIsSaving(true);
    try {
      const res = await createLeadAdmin(createFormData);
      const leadId = res.data?.id;

      // Upload logo if selected
      if (logoFile && leadId) {
        const formData = new FormData();
        formData.append("file", logoFile);
        await api.post(`/leads/${leadId}/logo`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }

      toast.success("Lead created & external user account provisioned");
      setIsCreateDialogOpen(false);
      setCreateFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        company_size: "",
        message: "",
        problem_interest: "",
        vertical_interest: ""
      });
      setLogoFile(null);
      setLogoPreview(null);
      fetchLeads();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || t("admin.leads.failedToCreate");
      toast.error(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const openEditDialog = (lead) => {
    setSelectedLead(lead);
    setEditNotes(lead.notes || "");
    setEditStatus(lead.status);
    setIsDialogOpen(true);
  };

  const filteredLeads = leads.filter((lead) => {
    const matchesSearch = 
      lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (lead.company || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || lead.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (isoString) => {
    if (!isoString) return "-";
    return new Date(isoString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-leads-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{t("admin.leads.title")}</h1>
            <p className="text-slate-500">{t("admin.leads.subtitle")}</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={fetchLeads} variant="outline" className="gap-2">
              <RefreshCw className="w-4 h-4" />
              {t("common.refresh")}
            </Button>
            <Button 
              onClick={() => setIsCreateDialogOpen(true)} 
              className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 gap-2"
              data-testid="create-lead-btn"
            >
              <Plus className="w-4 h-4" />
              {t("admin.leads.createLead")}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {["new", "contacted", "qualified", "converted", "lost"].map((status) => (
            <div key={status} className="bg-white rounded-lg border p-4">
              <p className="text-sm text-slate-500 capitalize">{t(`admin.leads.${status}`)}</p>
              <p className="text-2xl font-bold text-slate-900">
                {leads.filter((l) => l.status === status).length}
              </p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder={t("admin.leads.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={t("common.filter")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("admin.leads.allStatuses")}</SelectItem>
              <SelectItem value="new">{t("admin.leads.new")}</SelectItem>
              <SelectItem value="contacted">{t("admin.leads.contacted")}</SelectItem>
              <SelectItem value="qualified">{t("admin.leads.qualified")}</SelectItem>
              <SelectItem value="converted">{t("admin.leads.converted")}</SelectItem>
              <SelectItem value="lost">{t("admin.leads.lost")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">{t("common.loading")}</div>
          ) : filteredLeads.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>{t("admin.leads.noLeadsFound")}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("admin.leads.contact")}</TableHead>
                  <TableHead>{t("common.company")}</TableHead>
                  <TableHead>{t("admin.leads.interest")}</TableHead>
                  <TableHead>{t("common.status")}</TableHead>
                  <TableHead>{t("common.date")}</TableHead>
                  <TableHead className="w-[100px]">{t("common.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLeads.map((lead) => (
                  <TableRow key={lead.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-900">{lead.name}</p>
                        <div className="flex items-center gap-1 text-sm text-slate-500">
                          <Mail className="w-3 h-3" />
                          {lead.email}
                        </div>
                        <div className="flex items-center gap-1 text-sm text-slate-500">
                          <Phone className="w-3 h-3" />
                          {lead.phone}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Building className="w-4 h-4 text-slate-400" />
                        <span>{lead.company || "-"}</span>
                      </div>
                      {lead.company_size && (
                        <span className="text-sm text-slate-500">{lead.company_size} employees</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm font-medium">{lead.problem_interest || "-"}</p>
                        <p className="text-xs text-slate-500">{lead.vertical_interest}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[lead.status]}>
                        {lead.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {formatDate(lead.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => openEditDialog(lead)}
                          className="h-8 w-8"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleDeleteLead(lead.id)}
                          className="h-8 w-8 text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>{t("admin.leads.editLead")}</DialogTitle>
            </DialogHeader>
            {selectedLead && (
              <div className="space-y-4 pt-4">
                {/* Lead Info */}
                <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                  <p className="font-medium text-slate-900">{selectedLead.name}</p>
                  <div className="flex items-center gap-4 text-sm text-slate-600">
                    <span className="flex items-center gap-1">
                      <Mail className="w-3 h-3" /> {selectedLead.email}
                    </span>
                    <span className="flex items-center gap-1">
                      <Phone className="w-3 h-3" /> {selectedLead.phone}
                    </span>
                  </div>
                  {selectedLead.message && (
                    <div className="mt-2 pt-2 border-t">
                      <p className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                        <MessageSquare className="w-3 h-3" /> Message:
                      </p>
                      <p className="text-sm text-slate-700">{selectedLead.message}</p>
                    </div>
                  )}
                </div>

                {/* Status */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t("common.status")}</label>
                  <Select value={editStatus} onValueChange={setEditStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">{t("admin.leads.new")}</SelectItem>
                      <SelectItem value="contacted">{t("admin.leads.contacted")}</SelectItem>
                      <SelectItem value="qualified">{t("admin.leads.qualified")}</SelectItem>
                      <SelectItem value="converted">{t("admin.leads.converted")}</SelectItem>
                      <SelectItem value="lost">{t("admin.leads.lost")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t("admin.leads.internalNotes")}</label>
                  <Textarea
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    placeholder={t("admin.leads.addNotesPlaceholder")}
                    rows={3}
                  />
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                    {t("common.cancel")}
                  </Button>
                  <Button onClick={handleUpdateLead} className="bg-[#0066CC] hover:bg-[#0052A3]">
                    {t("admin.leads.saveChanges")}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Create Lead Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t("admin.leads.createNewLead")}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateLead} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="lead-name">{t("common.name")} *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="lead-name"
                      placeholder="Full name"
                      value={createFormData.name}
                      onChange={(e) => setCreateFormData({ ...createFormData, name: e.target.value })}
                      className="pl-10"
                      required
                      data-testid="lead-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lead-email">{t("common.email")} *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="lead-email"
                      type="email"
                      placeholder="email@company.com"
                      value={createFormData.email}
                      onChange={(e) => setCreateFormData({ ...createFormData, email: e.target.value })}
                      className="pl-10"
                      required
                      data-testid="lead-email-input"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="lead-phone">{t("common.phone")}</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="lead-phone"
                      placeholder="+91 98765 43210"
                      value={createFormData.phone}
                      onChange={(e) => setCreateFormData({ ...createFormData, phone: e.target.value })}
                      className="pl-10"
                      data-testid="lead-phone-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lead-company">{t("common.company")}</Label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="lead-company"
                      placeholder="Company name"
                      value={createFormData.company}
                      onChange={(e) => setCreateFormData({ ...createFormData, company: e.target.value })}
                      className="pl-10"
                      data-testid="lead-company-input"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("admin.leads.companySize")}</Label>
                  <Select
                    value={createFormData.company_size}
                    onValueChange={(value) => setCreateFormData({ ...createFormData, company_size: value })}
                  >
                    <SelectTrigger data-testid="lead-company-size-select">
                      <SelectValue placeholder={t("admin.leads.selectSize")} />
                    </SelectTrigger>
                    <SelectContent>
                      {companySizes.map((size) => (
                        <SelectItem key={size} value={size}>{size} {t("admin.leads.employees")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{t("admin.leads.industryInterest")}</Label>
                  <Select
                    value={createFormData.vertical_interest}
                    onValueChange={(value) => setCreateFormData({ ...createFormData, vertical_interest: value })}
                  >
                    <SelectTrigger data-testid="lead-vertical-select">
                      <SelectValue placeholder={t("admin.leads.selectIndustry")} />
                    </SelectTrigger>
                    <SelectContent>
                      {verticals.map((v) => (
                        <SelectItem key={v.id} value={v.name}>{v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="lead-message">{t("admin.leads.messageNotes")}</Label>
                <Textarea
                  id="lead-message"
                  placeholder={t("admin.leads.messageNotesPlaceholder")}
                  value={createFormData.message}
                  onChange={(e) => setCreateFormData({ ...createFormData, message: e.target.value })}
                  rows={3}
                  data-testid="lead-message-input"
                />
              </div>

              {/* Prospect Logo Upload */}
              <div className="space-y-2">
                <Label>Prospect Logo (JPEG/PNG)</Label>
                <div className="flex items-center gap-3">
                  {logoPreview ? (
                    <div className="relative w-16 h-16 rounded-lg border border-slate-200 overflow-hidden bg-white shrink-0">
                      <img src={logoPreview} alt="Logo" className="w-full h-full object-contain" />
                      <button type="button" onClick={() => { setLogoFile(null); setLogoPreview(null); }}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-blue-400 transition-colors flex-1" data-testid="lead-logo-upload">
                      <ImagePlus className="w-5 h-5 text-slate-400" />
                      <span className="text-sm text-slate-500">Upload logo</span>
                      <input type="file" accept="image/jpeg,image/png" className="hidden" onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setLogoFile(file);
                          setLogoPreview(URL.createObjectURL(file));
                        }
                      }} />
                    </label>
                  )}
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                <p className="text-xs text-blue-700">An external user account will be auto-created with default password <strong>Absolin</strong> for this lead.</p>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsCreateDialogOpen(false)}
                >
                  {t("common.cancel")}
                </Button>
                <Button 
                  type="submit" 
                  disabled={isSaving}
                  className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
                  data-testid="save-lead-btn"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t("admin.common.creating")}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {t("admin.leads.createLead")}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
